import React, { useState, useEffect } from 'react';
import { User, Lock, MapPin, Mail, AlertCircle, Check, Phone, Bell, Shield, Settings, Building2, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LANGUAGES } from '../i18n';
import { supabase } from '../lib/supabase';

type TabKey = 'profilo' | 'impostazioni' | 'azienda';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const { language, setLanguage, t } = useLanguage();

  const tabs: { key: TabKey; label: string; icon: React.ElementType }[] = [
    { key: 'profilo', label: t.profile.tabs.profile, icon: User },
    { key: 'impostazioni', label: t.profile.tabs.settings, icon: Settings },
    { key: 'azienda', label: t.profile.tabs.company, icon: Building2 },
  ];

  const [activeTab, setActiveTab] = useState<TabKey>('profilo');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    city: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [notifications, setNotifications] = useState({
    emailNotifiche: true,
    notifichePush: true,
    promemoriaEventi: true
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        city: user.city || ''
      }));
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const updates: any = {};

      if (formData.name !== user?.name) {
        updates.name = formData.name;
      }

      if (formData.phone !== user?.phone) {
        updates.phone = formData.phone;
      }

      if ((user?.role === 'organizer' || user?.role === 'pr') && formData.city !== user?.city) {
        updates.city = formData.city;
      }

      if (formData.newPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          throw new Error(t.profile.messages.passwordMismatch);
        }
        if (formData.newPassword.length < 6) {
          throw new Error(t.profile.messages.passwordTooShort);
        }
        updates.password = formData.newPassword;
      }

      if (Object.keys(updates).length > 0) {
        await updateUser(updates);
        setSuccess(t.profile.messages.updateSuccess);

        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      }
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.message || t.profile.messages.updateError);
    } finally {
      setLoading(false);
    }
  };

  const roleLabel = t.profile.roles[user?.role || 'collaboratore'];

  return (
    <div className="p-8">
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">{t.profile.header.name}</p>
                  <p className="text-2xl font-bold text-white mt-1">{user?.name}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <User className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">{t.profile.header.role}</p>
                  <p className="text-2xl font-bold text-white mt-1">{roleLabel}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Lock className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            {(user?.role === 'organizer' || user?.role === 'pr') && (
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm font-medium">{t.profile.header.city}</p>
                    <p className="text-2xl font-bold text-white mt-1">{user?.city || t.profile.header.notDefined}</p>
                  </div>
                  <div className="bg-white/20 p-3 rounded-lg">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-1 mb-8 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? 'bg-white text-rose-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-lg flex items-center gap-2">
          <Check className="h-5 w-5" />
          <p>{success}</p>
        </div>
      )}

      {activeTab === 'profilo' && (
        <div className="max-w-2xl">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t.profile.form.name}</label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      required
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">{t.profile.form.email}</label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      disabled
                      className="pl-10 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm cursor-not-allowed"
                      value={formData.email}
                    />
                  </div>
                  <p className="mt-1 text-sm text-gray-500">{t.profile.form.emailReadonly}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">{t.profile.form.phone}</label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder={t.profile.form.phonePlaceholder}
                    />
                  </div>
                </div>

                {(user?.role === 'organizer' || user?.role === 'pr') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t.profile.form.city}</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MapPin className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder={t.profile.form.cityPlaceholder}
                      />
                    </div>
                  </div>
                )}

                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">{t.profile.form.changePassword}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t.profile.form.newPassword}</label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="password"
                          className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                          value={formData.newPassword}
                          onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                          placeholder={t.profile.form.newPasswordPlaceholder}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t.profile.form.confirmPassword}</label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="password"
                          className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                          placeholder={t.profile.form.confirmPasswordPlaceholder}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-rose-600 text-white px-4 py-2 rounded-lg hover:bg-rose-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? t.profile.form.saving : t.profile.form.save}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'impostazioni' && (
        <div className="max-w-2xl space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-rose-50 rounded-lg">
                <Globe className="h-5 w-5 text-rose-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">{t.profile.settings.language}</h2>
                <p className="text-sm text-gray-500">{t.profile.settings.languageDescription}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all ${
                    language === lang.code
                      ? 'border-rose-500 bg-rose-50 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-2xl">{lang.flag}</span>
                  <span className={`font-medium ${language === lang.code ? 'text-rose-700' : 'text-gray-700'}`}>
                    {lang.label}
                  </span>
                  {language === lang.code && (
                    <Check className="h-4 w-4 text-rose-500 ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-rose-50 rounded-lg">
                <Bell className="h-5 w-5 text-rose-500" />
              </div>
              <h2 className="text-lg font-semibold">{t.profile.settings.notifications}</h2>
            </div>
            <div className="space-y-4">
              {([
                { key: 'emailNotifiche' as const, label: t.profile.settings.emailNotifications },
                { key: 'notifichePush' as const, label: t.profile.settings.pushNotifications },
                { key: 'promemoriaEventi' as const, label: t.profile.settings.eventReminders }
              ]).map((item) => (
                <div key={item.key} className="flex items-center justify-between">
                  <span className="text-gray-700">{item.label}</span>
                  <button
                    type="button"
                    onClick={() => setNotifications(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      notifications[item.key] ? 'bg-rose-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        notifications[item.key] ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-rose-50 rounded-lg">
                <Shield className="h-5 w-5 text-rose-500" />
              </div>
              <h2 className="text-lg font-semibold">{t.profile.settings.security}</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">{t.profile.settings.twoFactor}</span>
                <button
                  type="button"
                  onClick={() => setTwoFactorEnabled(prev => !prev)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    twoFactorEnabled ? 'bg-rose-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'azienda' && (
        <div className="max-w-2xl">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-rose-50 rounded-lg">
                <Building2 className="h-5 w-5 text-rose-500" />
              </div>
              <h2 className="text-lg font-semibold">{t.profile.company.title}</h2>
            </div>
            <div className="space-y-4">
              {[
                { label: t.profile.company.companyName, value: 'Speed Dating Italia' },
                { label: t.profile.company.email, value: 'info@speeddatingitalia.com' },
                { label: t.profile.company.phone, value: '+39 02 1234567' }
              ].map((field) => (
                <div key={field.label} className="flex items-center justify-between">
                  <span className="text-gray-700">{field.label}</span>
                  <span className="text-gray-900 font-medium">{field.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
